/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React, { useMemo, useState } from "react";
import PhoneNumber from "awesome-phonenumber";
import {
	Box,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TablePagination,
	TableRow,
	Typography,
	Paper,
	Checkbox,
	Chip,
	LinearProgress,
} from "@material-ui/core";
import AzureContactsTableToolbar from "./AzureContactsTableToolbar";
import AzureContactsTableHead from "./AzureContactsTableHead";
import usePagination from "../../../../../hooks/usePagination";
import { getComparator, stableSort } from "../../../../../utility/tableSort";

export const getPhoneNormalized = (user) => {
	let phone =
		user.mobilePhone || (user.businessPhones && user.businessPhones.length > 0 ? user.businessPhones[0] : null);
	if (phone) {
		const pn = new PhoneNumber(phone);
		if (pn.isValid()) {
			phone = pn.getNumber(); // e164 by default
		} else {
			phone = "";
		}
	}
	return phone || "";
};

const hasCommonElement = (srcArr = [], destArr = []) => !!srcArr.find((x) => destArr.includes(x));

const hasTermInObject = (user, term) =>
	!!Object.values(user).find((value) => String(value).toUpperCase().includes(String(term).toUpperCase()));

const ChipRenderer = ({ items }) =>
	items?.map
		? items.map((item) => (
				<Chip key={item} variant="outlined" size="small" label={item} style={{ margin: "2px 5px 2px 0" }} />
		  ))
		: "";

export default function AzureContactsTable({ usersLookup, groupsLookup, isSaving, selectedUsers, setSelectedUsers }) {
	const [order, setOrder] = React.useState("asc");
	const [orderBy, setOrderBy] = React.useState("");
	const [searchTerm, setSearchTerm] = React.useState("");
	const [selectedGroups, setSelectedGroups] = useState([]);

	const filteredUsers = useMemo(() => {
		return selectedGroups.length
			? usersLookup.filter((user) => hasTermInObject(user, searchTerm) && hasCommonElement(user.groups, selectedGroups))
			: usersLookup.filter((user) => hasTermInObject(user, searchTerm));
	}, [selectedGroups, searchTerm, usersLookup]);

	const { dataSlice, pageNo, rowsPerPage, setPageNo, setRowsPerPage } = usePagination(filteredUsers, 5);

	// Avoid a layout jump when reaching the last page with empty rows.
	const emptyRows = filteredUsers?.length === 0;

	const handleGroupSelect = (groups) => {
		setSelectedGroups(groups);
	};

	const handleRequestSort = (event, property) => {
		const isAsc = orderBy === property && order === "asc";
		setOrder(isAsc ? "desc" : "asc");
		setOrderBy(property);
	};

	const handleSelectAllClick = (event) => {
		if (event.target.checked) {
			const newSelected = filteredUsers.map((n) => n.id);
			setSelectedUsers(newSelected);
			return;
		}
		setSelectedUsers([]);
	};

	const handleClick = (event, userId) => {
		const selectedIndex = selectedUsers.indexOf(userId);
		let newSelected = [];

		if (selectedIndex === -1) {
			newSelected = newSelected.concat(selectedUsers, userId);
		} else if (selectedIndex === 0) {
			newSelected = newSelected.concat(selectedUsers.slice(1));
		} else if (selectedIndex === selectedUsers.length - 1) {
			newSelected = newSelected.concat(selectedUsers.slice(0, -1));
		} else if (selectedIndex > 0) {
			newSelected = newSelected.concat(selectedUsers.slice(0, selectedIndex), selectedUsers.slice(selectedIndex + 1));
		}
		setSelectedUsers(newSelected);
	};

	const isSelected = (name) => selectedUsers.indexOf(name) !== -1;

	return (
		<Box sx={{ width: "100%" }}>
			<Paper sx={{ width: "100%", mb: 2 }}>
				<AzureContactsTableToolbar
					selectedGroups={selectedGroups}
					setSelectedGroups={handleGroupSelect}
					numSelected={selectedUsers.length}
					groupsLookup={groupsLookup}
					searchTerm={searchTerm}
					setSearchTerm={setSearchTerm}
				/>
				<TableContainer>
					<Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle" size="small">
						<AzureContactsTableHead
							numSelected={selectedUsers.length}
							order={order}
							orderBy={orderBy}
							onSelectAllClick={handleSelectAllClick}
							onRequestSort={handleRequestSort}
							rowCount={usersLookup.length}
						/>
						<TableBody>
							{stableSort(dataSlice, getComparator(order, orderBy)).map((row, index) => {
								const isItemSelected = isSelected(row.id);
								const labelId = `enhanced-table-checkbox-${index}`;
								return (
									<TableRow
										hover
										onClick={(event) => handleClick(event, row.id)}
										role="checkbox"
										aria-checked={isItemSelected}
										tabIndex={-1}
										key={row.id}
									>
										<TableCell padding="checkbox">
											<Checkbox
												checked={isItemSelected}
												onChange={(event) => handleClick(event, row.id)}
												inputProps={{
													"aria-labelledby": labelId,
												}}
											/>
										</TableCell>
										<TableCell component="th" id={labelId} scope="row">
											{row.displayName}
										</TableCell>
										<TableCell>{getPhoneNormalized(row) || ""}</TableCell>
										<TableCell>
											<ChipRenderer items={row.groups} />
										</TableCell>
										<TableCell>{row.id}</TableCell>
									</TableRow>
								);
							})}
							{emptyRows && (
								<TableRow>
									<TableCell colSpan={5}>
										<Typography className="text-center text-muted" variant="body1">
											No users to display
										</Typography>
									</TableCell>
								</TableRow>
							)}
							{isSaving && (
								<TableRow style={{ padding: 0 }}>
									<TableCell colSpan={5} className="p-0">
										<LinearProgress />
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</TableContainer>
				<TablePagination
					rowsPerPageOptions={[5, 10, 25]}
					component="div"
					count={filteredUsers.length}
					rowsPerPage={rowsPerPage}
					page={pageNo}
					onPageChange={(_e, pageNo) => setPageNo(pageNo)}
					onRowsPerPageChange={(rowsPerPageCnt) => {
						setRowsPerPage(parseInt(rowsPerPageCnt.currentTarget.value, 10) || 10);
						setPageNo(0);
					}}
				/>
			</Paper>
		</Box>
	);
}
